import { Checkbox, Modal, Switch, Table } from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import * as React from "react";
import { getTranslation } from "../../../../Actions/VerbiageActions";
import { AppArea } from "../../../../Enums/AppArea";
import { TerritoryTypePermission } from "../../../../Enums/Enums";
import {
    ITerritoryType,
    ITerritoryTypePermission,
    IUser,
} from "../../../../Models/Models";

interface ITerritoryManagementProps {
  user: IUser;
  territoryTypePermissions: Array<ITerritoryTypePermission>;
  //companyTerritoryTypes: Array<ITerritoryType>;

  updateTerritoryTypeAdminOnlyProperty: (territoryType: ITerritoryType) => void;
  updateTerritoryTypeUserPermissions: (
    userID: number,
    territoryTypeID: number,
    canView: boolean,
    canEdit: boolean
  ) => void;
  getTerritoryTypePermissions: (territoryTypeID: number) => void;
  updateTerritoryTypeUserPermissionsForAll: (
    territoryPermissions: Array<ITerritoryTypePermission>
  ) => void;
  getCompanyTerritoryTypes: () => void;
}

interface ITerritoryManagementState {
  allTerritoryTypes: Array<ITerritoryType>;
  selectedTerritoryType: ITerritoryType;
  modalVisible: boolean;
  loadingTerritoryTypePermissions: boolean;
}

interface IUserTerritoryPermissions extends IUser {
  userID: number;
  tTypeID: number;
  canEdit: boolean;
  canView: boolean;
}

interface IUserCopy extends IUser {
  // for Ant Design table
  userType: string;
  loginStatusString: string;
  showingActiveStatusMenu: boolean;
}

class TerritoryManagement extends React.Component<
  ITerritoryManagementProps,
  ITerritoryManagementState
> {
  private allNonAdminUsers: IUser[] = [];
  private allAdminUsers: IUser[] = [];
  private readonly territoryTypeColumns: any[] = [
      {
          title: null,
          dataIndex: null,
          width: 50,
          render: (territoryType: ITerritoryType): JSX.Element => (
              <div
                  style={{
                      height: "24px",
                      width: "24px",
                      border: "2px solid",
                      borderColor: territoryType.colorStrokeHex,
                  }}
              >
                  <div
                      style={{
                          height: "100%",
                          opacity: 0.2,
                          backgroundColor: territoryType.colorFillHex,
                      }}
                  ></div>
              </div>
          ),
      },
      {
          title:
        getTranslation(
            this.props.user.company.companyType,
            AppArea.TERRITORIES,
            false
        ) + " Type",
          dataIndex: "name",
          sorter: (a: ITerritoryType, b: ITerritoryType) =>
              a.name && b.name && a.name.toLowerCase() < b.name.toLowerCase()
                  ? -1
                  : 1,
      },
      {
          title: null,
          dataIndex: null, // what will show up in the UI
          render: (territoryType: ITerritoryType) => (
              <div>
                  <button
                      className="manage-button right"
                      onClick={(): void => this.manageTerritoryType(territoryType)}
                  >
            Manage
                  </button>
                  <div className="clear"></div>
              </div>
          ),
      },
  ];

  /**
   *
   */
  constructor(props) {
      super(props);
      this.state = {
          allTerritoryTypes: [...this.props.user.company.territoryTypes.types],
          selectedTerritoryType: null,
          modalVisible: false,
          loadingTerritoryTypePermissions: false,
      };
      this.allNonAdminUsers = this.props.user.company.users.filter(
          (user: IUser) => !user.isAdmin
      );
      this.allAdminUsers = this.props.user.company.users.filter(
          (user: IUser) => user.isAdmin
      );
  }

  public componentDidMount() {
      this.props.getCompanyTerritoryTypes();
  }

  public componentWillReceiveProps(newProps: ITerritoryManagementProps) {
      if (this.state.loadingTerritoryTypePermissions) {
          this.setState({
              loadingTerritoryTypePermissions: false,
          });
      }
      if (newProps && newProps.user) {
          this.setState({
              allTerritoryTypes: [...newProps.user.company.territoryTypes.types],
          });
      }
  }

  public render() {
      const userTypes = this.allNonAdminUsers.map((user: IUserCopy): string => {
          if (user.isAdmin) {
              user.userType = "Admin";
          } else if (user.isFranchisee) {
              user.userType = "Franchisee";
          } else {
              user.userType = "Standard";
          }
          return user.userType;
      });
      // get unique user types - ["Admin", Standard", "Franchisee"]
      const uniqueUserTypes = [...new Set(userTypes)]; // ts yells...but it works and it compiles
      // create object for Ant-Design Filtering
      const uniqueUserTypeFilters = uniqueUserTypes.map((type: string) => {
          return {
              text: type,
              value: type,
          };
      });

      const manageTerritoryColumns: Array<any> = [
          {
              title: "First Name",
              dataIndex: "firstName",
              defaultSortOrder: "ascend",
              sorter: (a: IUserTerritoryPermissions, b: IUserTerritoryPermissions) =>
                  a.firstName
          && b.firstName
          && a.firstName.toLowerCase() < b.firstName.toLowerCase()
                      ? -1
                      : 1,
          },
          {
              title: "Last Name",
              dataIndex: "lastName",
              defaultSortOrder: "ascend",
              sorter: (a: IUserTerritoryPermissions, b: IUserTerritoryPermissions) =>
                  a.lastName
          && b.lastName
          && a.lastName.toLowerCase() < b.lastName.toLowerCase()
                      ? -1
                      : 1,
          },
          {
              title: "User Type",
              dataIndex: "userType",
              filters: uniqueUserTypeFilters,
              filterMultiple: true,
              defaultSortOrder: "ascend",
              onFilter: (value, record) => record.userType.indexOf(value) === 0,
              sorter: (a: IUserCopy, b: IUserCopy) =>
                  a.userType
          && b.userType
          && a.userType.toLowerCase() < b.userType.toLowerCase()
                      ? -1
                      : 1,
          },
          {
              title: null,
              dataIndex: null,
              width: 50,
              sorter: (a: IUserTerritoryPermissions, b: IUserTerritoryPermissions) =>
                  a.canView < b.canView ? -1 : 1,
              render: (user: IUserTerritoryPermissions) => (
                  <div style={{ width: "140px", textAlign: "right" }}>
                      <Checkbox
                          checked={this.userCanView(user)}
                          onChange={(event: CheckboxChangeEvent): void =>
                              this.territoryPermissionsOptionsChanged(
                                  user,
                                  event,
                                  TerritoryTypePermission.VIEW
                              )
                          }
                          disabled={this.state.selectedTerritoryType.isAdminOnly}
                      >
              View
                      </Checkbox>
                  </div>
              ),
          },
          {
              title: null,
              dataIndex: null,
              width: 50,
              sorter: (a: IUserTerritoryPermissions, b: IUserTerritoryPermissions) =>
                  a.canEdit < b.canEdit ? -1 : 1,
              render: (user: IUserTerritoryPermissions) => (
                  <div style={{ width: "140px", textAlign: "right" }}>
                      <Checkbox
                          checked={this.userCanEdit(user)}
                          onChange={(event: CheckboxChangeEvent): void =>
                              this.territoryPermissionsOptionsChanged(
                                  user,
                                  event,
                                  TerritoryTypePermission.EDIT
                              )
                          }
                          disabled={this.state.selectedTerritoryType.isAdminOnly}
                      >
              Edit
                      </Checkbox>
                  </div>
              ),
          },
      ];

      let territoryModal: JSX.Element | null = null;
      if (this.state.selectedTerritoryType) {
          territoryModal = (
              <Modal
                  title={this.state.selectedTerritoryType.name}
                  visible={this.state.modalVisible}
                  onOk={this.handleModalOkClick}
                  onCancel={this.handleModalCancelClick}
                  footer={null}
                  width={800}
                  centered={true}
              >
                  <div
                      style={{
                          marginBottom: "1rem",
                          display: "flex",
                          alignItems: "center",
                      }}
                  >
                      <Switch
                          checked={this.state.selectedTerritoryType.isAdminOnly}
                          onChange={() =>
                              this.toggleTerritoryAdminMode(this.state.selectedTerritoryType)
                          }
                      ></Switch>
                      <span style={{ marginLeft: "1rem" }}>
                          <b>Admin Only</b>
                      </span>
                      <div style={{ marginLeft: "auto" }}>
                          <Switch
                              checked={this.allUsersCanView(
                                  this.props.territoryTypePermissions
                              )}
                              onChange={(): void =>
                                  this.toggleAllUsersPermissions(
                                      this.state.selectedTerritoryType,
                                      TerritoryTypePermission.VIEW
                                  )
                              }
                              disabled={this.state.selectedTerritoryType.isAdminOnly}
                          ></Switch>
                          <span style={{ margin: "0 4rem 0 1rem" }}>
                              <b>All View</b>
                          </span>
                          <Switch
                              checked={this.allUsersCanEdit(
                                  this.props.territoryTypePermissions
                              )}
                              onChange={(): void =>
                                  this.toggleAllUsersPermissions(
                                      this.state.selectedTerritoryType,
                                      TerritoryTypePermission.EDIT
                                  )
                              }
                              disabled={this.state.selectedTerritoryType.isAdminOnly}
                          ></Switch>
                          <span style={{ marginLeft: "1rem" }}>
                              <b>All Edit</b>
                          </span>
                      </div>
                  </div>

                  <Table
                      columns={manageTerritoryColumns}
                      dataSource={this.allNonAdminUsers}
                      onChange={this.filterChange}
                      rowKey={(user: IUser): string => user.userID.toString()}
                      loading={this.state.loadingTerritoryTypePermissions}
                  ></Table>
              </Modal>
          );
      }

      let territoryTypesTable: JSX.Element | null = null;
      if (this.props.user.company.territoryTypes.types.length > 0) {
          territoryTypesTable = (
              <Table
                  columns={this.territoryTypeColumns}
                  dataSource={this.state.allTerritoryTypes}
                  onChange={this.filterChange}
                  rowKey={(tType: ITerritoryType): string => tType.typeID.toString()}
              ></Table>
          );
      }

      const territoryWord = getTranslation(
          this.props.user.company.companyType,
          AppArea.TERRITORIES,
          true
      );

      return (
          <div className="TerritoryManagement">
              {territoryModal}

              <div className="top-container">
                  <h2 className="settings-title">{territoryWord}</h2>
                  <input
                      id="territory-management-search"
                      type="search"
                      placeholder="Search for type..."
                      onChange={this.territorySearchInputChanged}
                  />
              </div>

              {territoryTypesTable}
          </div>
      );
  }

  private toggleAllUsersPermissions = (
      territoryType: ITerritoryType,
      action: TerritoryTypePermission
  ) => {
      this.setLoadingTerritoryTypePermissions(true);

      const permissionsCopy: ITerritoryTypePermission[] = [
          ...this.props.territoryTypePermissions,
      ];

      // add every user to the array with no permission
      this.allNonAdminUsers.forEach((user: IUser) => {
          const foundPermission: ITerritoryTypePermission = permissionsCopy.find(
              (permission: ITerritoryTypePermission) => {
                  return permission.userID === user.userID;
              }
          );
          if (!foundPermission) {
              permissionsCopy.push({
                  typeID: this.state.selectedTerritoryType.typeID,
                  userID: user.userID,
                  canView: false,
                  canEdit: false,
              });
          }
      });

      switch (action) {
          case TerritoryTypePermission.VIEW:
              if (this.allUsersCanView(permissionsCopy)) {
                  // take away view from everyone
                  permissionsCopy.forEach((permission: ITerritoryTypePermission) => {
                      let isAdmin = false;
                      for (let i = 0; i < this.allAdminUsers.length; i++) {
                          if (this.allAdminUsers[i].userID === permission.userID) {
                              isAdmin = true;
                              break;
                          }
                      }
                      if (!isAdmin) {
                          permission.canView = false;
                      }
                  });
              } else {
                  // give view to everyone
                  permissionsCopy.forEach((permission: ITerritoryTypePermission) => {
                      permission.canView = true;
                  });
              }
              break;
          case TerritoryTypePermission.EDIT:
              if (this.allUsersCanEdit(permissionsCopy)) {
                  // take away edit from everyone
                  permissionsCopy.forEach((permission: ITerritoryTypePermission) => {
                      let isAdmin = false;
                      for (let i = 0; i < this.allAdminUsers.length; i++) {
                          if (this.allAdminUsers[i].userID === permission.userID) {
                              isAdmin = true;
                              break;
                          }
                      }
                      if (!isAdmin) {
                          permission.canEdit = false;
                      }
                  });
              } else {
                  // give edit to everyone
                  permissionsCopy.forEach((permission: ITerritoryTypePermission) => {
                      permission.canEdit = true;
                  });
              }
              break;
      }

      this.props.updateTerritoryTypeUserPermissionsForAll(permissionsCopy);
  };

  private userCanView = (user: IUserTerritoryPermissions) => {
      for (let i = 0; i < this.props.territoryTypePermissions.length; i++) {
          if (
              this.props.territoryTypePermissions[i].userID === user.userID
        && this.props.territoryTypePermissions[i].canView
          ) {
              return true;
          }
      }
      return false;
  };
  private userCanEdit = (user: IUserTerritoryPermissions) => {
      for (let i = 0; i < this.props.territoryTypePermissions.length; i++) {
          if (
              this.props.territoryTypePermissions[i].userID === user.userID
        && this.props.territoryTypePermissions[i].canEdit
          ) {
              return true;
          }
      }
      return false;
  };
  private allUsersCanView = (permissions: Array<ITerritoryTypePermission>) => {
      const permissionsCopy: Array<ITerritoryTypePermission> = [...permissions];
      // add every user to the array with no permission
      this.allNonAdminUsers.forEach((user: IUser) => {
          const foundPermission: ITerritoryTypePermission = permissionsCopy.find(
              (permission: ITerritoryTypePermission) => {
                  return permission.userID === user.userID;
              }
          );
          if (!foundPermission) {
              permissionsCopy.push({
                  typeID: this.state.selectedTerritoryType.typeID,
                  userID: user.userID,
                  canView: false,
                  canEdit: false,
              });
          }
      });

      for (let i = 0; i < permissionsCopy.length; i++) {
          if (!permissionsCopy[i].canView) {
              return false;
          }
      }
      return true;
  };
  private allUsersCanEdit = (permissions: Array<ITerritoryTypePermission>) => {
      const permissionsCopy: Array<ITerritoryTypePermission> = [...permissions];
      // add every user to the array with no permission
      this.allNonAdminUsers.forEach((user: IUser) => {
          const foundPermission: ITerritoryTypePermission = permissionsCopy.find(
              (permission: ITerritoryTypePermission) => {
                  return permission.userID === user.userID;
              }
          );
          if (!foundPermission) {
              permissionsCopy.push({
                  typeID: this.state.selectedTerritoryType.typeID,
                  userID: user.userID,
                  canView: false,
                  canEdit: false,
              });
          }
      });

      for (let i = 0; i < permissionsCopy.length; i++) {
          if (!permissionsCopy[i].canEdit) {
              return false;
          }
      }
      return true;
  };

  private manageTerritoryType = (territoryType: ITerritoryType): void => {
      this.setSelectedTerritoryType(territoryType);
      this.props.getTerritoryTypePermissions(territoryType.typeID);
      this.setModalVisible(true);
  };

  private setSelectedTerritoryType = (territoryType: ITerritoryType): void => {
      this.setState({
          selectedTerritoryType: territoryType,
      });
  };

  private handleModalCancelClick = (): void => {
      this.setModalVisible(false);
  };
  private handleModalOkClick = (): void => {
      this.setModalVisible(false);
  };

  private setModalVisible = (visible: boolean): void => {
      this.setState({
          modalVisible: visible,
      });
  };

  private setLoadingTerritoryTypePermissions = (loading: boolean) => {
      this.setState({
          loadingTerritoryTypePermissions: loading,
      });
  };

  private territoryPermissionsOptionsChanged = (
      user: IUserTerritoryPermissions,
      event: CheckboxChangeEvent,
      permissionType: TerritoryTypePermission
  ): void => {
      switch (permissionType) {
          case TerritoryTypePermission.VIEW:
              if (event.target.checked) {
                  user.canView = true;
              } else {
                  user.canView = false;
              }
              // set what they already have to send up to the server
              user.canEdit = this.userCanEdit(user);
              break;
          case TerritoryTypePermission.EDIT:
              if (event.target.checked) {
                  user.canEdit = true;
              } else {
                  user.canEdit = false;
              }
              // set what they already have to send up to the server
              user.canView = this.userCanView(user);
              break;
      }

      this.setLoadingTerritoryTypePermissions(true);

      user.tTypeID = this.state.selectedTerritoryType.typeID;

      this.props.updateTerritoryTypeUserPermissions(
          user.userID,
          this.state.selectedTerritoryType.typeID,
          user.canView,
          user.canEdit
      );
  };

  private toggleTerritoryAdminMode = (territoryType: ITerritoryType): void => {
      this.setLoadingTerritoryTypePermissions(true);

      const tTypeCopy: ITerritoryType = { ...territoryType };
      tTypeCopy.isAdminOnly = !tTypeCopy.isAdminOnly;

      this.props.updateTerritoryTypeAdminOnlyProperty(tTypeCopy);

      this.setState({
          selectedTerritoryType: tTypeCopy,
      });
      this.setLoadingTerritoryTypePermissions(false);
  };

  private territorySearchInputChanged = (
      event: React.ChangeEvent<HTMLInputElement>
  ): void => {
      const searchTerm: string = event.target.value.toLowerCase();

      if (searchTerm && searchTerm.length > 0) {
          const territoryTypeCopy: Array<ITerritoryType> = [];
          for (
              let i = 0;
              i < this.props.user.company.territoryTypes.types.length;
              i++
          ) {
              if (
                  this.props.user.company.territoryTypes.types[i].name
                      .toLowerCase()
                      .indexOf(searchTerm) > -1
              ) {
                  territoryTypeCopy.push(
                      this.props.user.company.territoryTypes.types[i]
                  );
              }
          }

          this.setState({
              allTerritoryTypes: territoryTypeCopy,
          });
      } else {
          this.setState({
              allTerritoryTypes: this.props.user.company.territoryTypes.types,
          });
      }
  };

  private setUserTerritoryPermissionsUsersInitialValues =
    (): Array<IUserTerritoryPermissions> => {
        const usersWithTerritoryPermissions: Array<IUserTerritoryPermissions> = [
            ...(this.allNonAdminUsers as Array<IUserTerritoryPermissions>),
        ];

        this.props.territoryTypePermissions.forEach(
            (permission: ITerritoryTypePermission, permissionIndex: number) => {
                usersWithTerritoryPermissions.forEach(
                    (user: IUserTerritoryPermissions, userIndex: number) => {
                        if (user.userID === permission.userID) {
                            user.canEdit = permission.canEdit;
                            user.canView = permission.canView;
                        }
                    }
                );
            }
        );

        return usersWithTerritoryPermissions;
    };

  private filterChange(pagination, filters, sorter) {
      console.log("params", pagination, filters, sorter);
  }
}

export default TerritoryManagement;
